/**
 * Workspace Store
 * 
 * Zustand store for workspace panel states.
 * Manages tool windows like Files explorer with persistence.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ResearchLabTab } from '../components/research-lab/types';

const STORAGE_KEY = 'quanthop-workspace';

/**
 * Navigation command from ActivityRail to StrategyWorkspace.
 * When set, the workspace should navigate to this tab.
 * null = no pending navigation
 */
type NavigationCommand = ResearchLabTab | null;

interface WorkspaceStore {
    // Files panel state
    filesOpen: boolean;
    filesWidth: number;
    
    // Navigation command - set by ActivityRail, consumed by StrategyWorkspace
    navigationCommand: NavigationCommand;
    
    // Current active tab - set by StrategyWorkspace, read by ActivityRail for visual feedback
    currentTab: ResearchLabTab | null;
    
    // Actions
    toggleFiles: () => void;
    setFilesOpen: (open: boolean) => void;
    setFilesWidth: (width: number) => void;
    closeFiles: () => void;
    openFiles: () => void;
    
    // Navigation actions
    navigateToTab: (tab: ResearchLabTab) => void;
    clearNavigationCommand: () => void;
    setCurrentTab: (tab: ResearchLabTab | null) => void;
}

const DEFAULT_FILES_WIDTH = 220;
const MIN_FILES_WIDTH = 150;
const MAX_FILES_WIDTH = 320;

export const useWorkspaceStore = create<WorkspaceStore>()(
    persist(
        (set) => ({
            // Initial state - files open by default for first-time users
            filesOpen: true,
            filesWidth: DEFAULT_FILES_WIDTH,
            navigationCommand: null,
            currentTab: null,
            
            // Actions
            toggleFiles: () => set((state) => ({ filesOpen: !state.filesOpen })),
            setFilesOpen: (open) => set({ filesOpen: open }),
            setFilesWidth: (width) => set({ 
                filesWidth: Math.max(MIN_FILES_WIDTH, Math.min(MAX_FILES_WIDTH, width)) 
            }),
            closeFiles: () => set({ filesOpen: false }),
            openFiles: () => set({ filesOpen: true }),
            
            // Navigation - command pattern for cross-component communication
            navigateToTab: (tab) => set({ navigationCommand: tab }),
            clearNavigationCommand: () => set({ navigationCommand: null }),
            setCurrentTab: (tab) => set({ currentTab: tab }),
        }),
        {
            name: STORAGE_KEY,
            partialize: (state) => ({
                filesOpen: state.filesOpen,
                filesWidth: state.filesWidth,
                // Don't persist navigationCommand or currentTab - they're ephemeral
            }),
        }
    )
);

// Selector hooks for performance
export const useFilesOpen = () => useWorkspaceStore((s) => s.filesOpen);
export const useFilesWidth = () => useWorkspaceStore((s) => s.filesWidth);
export const useToggleFiles = () => useWorkspaceStore((s) => s.toggleFiles);

// Export constants
export { MIN_FILES_WIDTH, MAX_FILES_WIDTH, DEFAULT_FILES_WIDTH };
